import classNames from 'classnames';
import debounce from 'lodash/debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';
import { DateRange, rangeConfig } from 'src/components/DateRange';
import LoadingIndicator from 'src/components/LoadingIndicator';
import NavTab from 'src/components/NavTab';
import { TablePaginationBtns } from 'src/components/PaginationBtns';
import { PaymentMethodSelector } from 'src/components/ShipmentInvoice';
import emptyImg from 'src/images/empty.svg';
import logo from 'src/images/logo-md.png';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Nav,
  Row,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  UncontrolledTooltip,
  useApp,
  useScreenSize,
  useUrlState
} from 'straightline-ui';
import {
  calculatePaymentFee,
  formatMoney
} from 'straightline-utils/accounting';
import {
  INVOICE_STATUS,
  MODE,
  PAYMENT_METHOD
} from 'straightline-utils/constants';
import {
  formatDate,
  formatDistanceToNow,
  startOfDay
} from 'straightline-utils/time';

const statusColor = {
  PAID: 'soft-success',
  OPEN: 'soft-primary',
  VOID: 'soft-secondary',
  UNCOLLECTIBLE: 'soft-danger'
};

const defaultSort = {
  due_date: -1
};

function truncateText(text) {
  if (!text) {
    return '';
  }

  if (text.length <= 20) {
    return text;
  }

  return text.substr(0, 20) + '...';
}

export default function ShipmentInvoices() {
  const app = useApp();
  const queryState = useUrlState();
  const tab = queryState.getInState('tab', 'all');
  const urlQuery = queryState.getInState('query', {});
  const [batchPay, setBatchPay] = useState(null);

  const query = useMemo(() => {
    const query = { $sort: defaultSort, ...urlQuery };

    if (tab === 'overdue') {
      query.due_date = { $lte: formatDate(startOfDay(new Date()), 'dateOnly') };
      query.status = INVOICE_STATUS.OPEN;
    } else if (tab === 'upcoming') {
      query.due_date = { $gte: formatDate(startOfDay(new Date()), 'dateOnly') };
      query.status = INVOICE_STATUS.OPEN;
    }

    if (batchPay) {
      query.status = INVOICE_STATUS.OPEN;
    }

    return query;
  }, [tab, urlQuery, batchPay]);

  const {
    result: invoices,
    isLoading,
    isError,
    error,
    isFetching
  } = app
    .service('api/shipment-invoices')
    .useFind({ query, $include: ['shipment'] }, { keepPreviousData: true });

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            <h1>{error.message || 'There was an unexpected error'}</h1>
            <Button to="/dashboard" color="primary" size="lg" className="mr-3">
              <Icon type="home" className="mr-2" />
              Return to Dashboard
            </Button>
            <Button to="/support" color="secondary" size="lg">
              <Icon type="send" className="mr-2" />
              Contact Straightline
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div>
      <Container fluid className="pb-5">
        <InvoicesHeader queryState={queryState} query={query} />
        <Row className="justify-content-center">
          <Col>
            <Card>
              <SearchBar
                tab={tab}
                queryState={queryState}
                query={query}
                total={invoices.total}
                batchPay={batchPay}
                setBatchPay={setBatchPay}
              />
              <div
                className={isFetching ? 'is-fetching' : ''}
                style={{
                  pointerEvents: isFetching ? 'none' : 'auto'
                }}
              >
                <BatchPayInvoices
                  batchPay={batchPay}
                  setBatchPay={setBatchPay}
                />
                <InvoicesTable
                  invoices={invoices}
                  query={query}
                  queryState={queryState}
                  batchPay={batchPay}
                  setBatchPay={setBatchPay}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

function InvoicesHeader({ queryState, query }) {
  const app = useApp();
  const { isXs, isSm, isMd, md } = useScreenSize();
  const tab = queryState.getInState('tab', 'all');

  const handleTabClick = async (tab) => {
    const { ...newQuery } = queryState.getInState('query', {});

    delete newQuery.$skip;
    delete newQuery.$limit;
    delete newQuery.due_date;
    delete newQuery.status;

    queryState.setState({ tab, query: newQuery });
  };

  const LabelTag = isXs || isSm || isMd ? 'h3' : 'h1';
  const labelStyle = {
    minWidth: md ? '80px' : ''
  };

  const statsQuery = { ...query };
  delete statsQuery.$skip;
  delete statsQuery.$limit;

  const { result: stats } = app
    .service('api/shipment-invoice-stats')
    .useFind({ query: statsQuery }, { keepPreviousData: true });

  const totals = stats?.total;

  return (
    <div className="header pt-md-2">
      <div className="header-body">
        <Row className="justify-content-between mb-n3">
          <Col xs="auto" className="mb-3">
            <h6 className="header-pretitle">Overview</h6>
            <LabelTag className="header-title">Invoices</LabelTag>
          </Col>
          <Col className="col-auto mb-3">
            <Row className="mb-n2">
              <Col xs="auto" className="text-center mb-2">
                <h6 className="header-pretitle">Total Billed</h6>
                <LabelTag className="header-title" style={labelStyle}>
                  {totals && (
                    <span
                      className="animated fadeIn"
                      key={totals.due_amount.sum}
                    >
                      {formatMoney(totals.due_amount.sum, {
                        precision: 0
                      })}
                    </span>
                  )}
                </LabelTag>
              </Col>
              <Col xs="auto" className="text-center mb-2">
                <h6 className="header-pretitle">Total Fees</h6>
                <LabelTag className="header-title" style={labelStyle}>
                  {totals && (
                    <span
                      className="animated fadeIn"
                      key={totals.fee_amount.sum}
                    >
                      {formatMoney(totals.fee_amount.sum, {
                        precision: 0
                      })}
                    </span>
                  )}
                </LabelTag>
              </Col>
              <Col xs="auto" className="text-center mb-2">
                <h6 className="header-pretitle">Total Paid</h6>
                <LabelTag className="header-title" style={labelStyle}>
                  {totals && (
                    <span
                      className="animated fadeIn"
                      key={totals.paid_amount.sum + totals.fee_amount.sum}
                    >
                      {formatMoney(
                        totals.paid_amount.sum + totals.fee_amount.sum,
                        {
                          precision: 0
                        }
                      )}
                    </span>
                  )}
                </LabelTag>
              </Col>
              <Col xs="auto" className="text-center mb-2">
                <h6 className="header-pretitle">Total Due</h6>
                <LabelTag className="header-title" style={labelStyle}>
                  {totals && (
                    <span
                      className="animated fadeIn"
                      key={totals.outstanding_amount.sum}
                    >
                      {totals.outstanding_amount.sum > 0
                        ? formatMoney(totals.outstanding_amount.sum, {
                            precision: 0
                          })
                        : formatMoney(0, { precision: 0 })}
                    </span>
                  )}
                </LabelTag>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col>
            <Nav tabs className="nav-overflow header-tabs">
              <NavTab tab="all" onClick={handleTabClick} activeTab={tab}>
                All Invoices
              </NavTab>
              <NavTab tab="upcoming" onClick={handleTabClick} activeTab={tab}>
                Upcoming
              </NavTab>
              <NavTab tab="overdue" onClick={handleTabClick} activeTab={tab}>
                Overdue
              </NavTab>
            </Nav>
          </Col>
        </Row>
      </div>
    </div>
  );
}

function BatchPayInvoices({ batchPay, setBatchPay }) {
  const app = useApp();
  const [modalOpen, setModalOpen] = useState(false);

  const { result: paymentMethods } = app
    .service('api/payment-methods')
    .useFind({
      query: {
        $limit: 100,
        type: { $in: [PAYMENT_METHOD.CARD, PAYMENT_METHOD.BANK_ACCOUNT] },
        $sort: { created_at: -1 }
      }
    });

  if (!batchPay) {
    return null;
  }

  const validPaymentMethods = paymentMethods?.data.filter((method) => {
    if (!method.exp_month || !method.exp_year) {
      return true;
    }
    const methodExpDate = new Date(
      Number(method.exp_year),
      Number(method.exp_month) - 1,
      1
    );
    const now = new Date();
    // Set to end of month
    methodExpDate.setMonth(methodExpDate.getMonth() + 1);
    methodExpDate.setDate(0);
    return methodExpDate > now;
  });

  return (
    <>
      <BatchPayInvoicesModal
        batchPay={batchPay}
        setBatchPay={setBatchPay}
        open={modalOpen}
        setOpen={setModalOpen}
        paymentMethods={validPaymentMethods}
      />
      <div className="border-top p-3 bg-primary-soft">
        <Row className="align-items-center justify-content-between mb-n2">
          <Col sm="auto" className="mb-2">
            <div>
              Select multiple invoices to pay at once. Save money on payment
              processing fees by paying in batches.
            </div>
          </Col>
          <Col sm="auto" className="mb-2">
            <div>
              {validPaymentMethods?.length === 0 ? (
                <Button
                  color="primary"
                  size="sm"
                  to="/settings/billing"
                  style={{ minWidth: 125 }}
                >
                  Add payment method
                </Button>
              ) : (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => setModalOpen(true)}
                  style={{ minWidth: 125 }}
                  disabled={batchPay.length === 0}
                >
                  Pay {batchPay.length} Invoice
                  {batchPay.length === 1 ? '' : 's'}
                </Button>
              )}

              <Button
                color="danger"
                size="sm"
                className="ml-2"
                onClick={() => {
                  setModalOpen(false);
                  setBatchPay(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
}

function BatchPayInvoicesModal({
  open,
  setOpen,
  batchPay,
  setBatchPay,
  paymentMethods
}) {
  return (
    <Modal
      isOpen={open}
      size="lg"
      contentClassName="h-100"
      modalClassName="modal-fullscreen-md"
      scrollable
      centered
    >
      {() => (
        <BatchPayInvoicesModalContent
          hide={() => setOpen(false)}
          batchPay={batchPay}
          setBatchPay={setBatchPay}
          paymentMethods={paymentMethods}
        />
      )}
    </Modal>
  );
}

function BatchPayInvoicesModalContent({
  hide,
  batchPay,
  setBatchPay,
  paymentMethods
}) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  useEffect(() => {
    if (paymentMethods?.length > 0 && !selectedPaymentMethod) {
      const defaultMethod = paymentMethods.find((method) => {
        return method.default_method;
      });
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod);
      }
    }
  }, [paymentMethods, selectedPaymentMethod]);

  const outstandingAmount = batchPay.reduce((sum, invoice) => {
    return sum + (invoice.due_amount - invoice.paid_amount);
  }, 0);

  const invalidInvoices = batchPay.filter((invoice) => {
    if (invoice.status !== INVOICE_STATUS.OPEN) {
      return true;
    }

    if (invoice.paid_amount >= invoice.due_amount) {
      return true;
    }

    const amount = invoice.due_amount - invoice.paid_amount;

    if (amount > invoice.due_amount) {
      return true;
    }

    return false;
  });

  if (invalidInvoices.length > 0) {
    return (
      <>
        <div className="p-4 border-bottom">
          <Row className="align-items-center justify-content-between">
            <Col xs="auto">
              <img
                src={logo}
                alt="..."
                className="img-fluid"
                style={{ maxHeight: 26 }}
              />
            </Col>
            <Col xs="auto">
              <Row className="align-items-center">
                <Col>
                  <div className="text-center">
                    <h6 className="header-pretitle">Invalid Invoices</h6>
                    <h3 className="header-title">
                      <span>{invalidInvoices.length}</span>
                    </h3>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
        <InvalidInvoices
          invalidInvoices={invalidInvoices}
          batchPay={batchPay}
          hide={hide}
          setBatchPay={setBatchPay}
        />
      </>
    );
  }

  return (
    <>
      <div className="p-4 border-bottom">
        <Row className="align-items-center justify-content-between">
          <Col xs="auto">
            <img
              src={logo}
              alt="..."
              className="img-fluid"
              style={{ maxHeight: 26 }}
            />
          </Col>
          <Col xs="auto">
            <Row className="align-items-center">
              <Col>
                <div className="text-center">
                  <h6 className="header-pretitle">Invoices</h6>
                  <h3 className="header-title">
                    <span className="animated fadeIn" key={batchPay.length}>
                      {batchPay.length}
                    </span>
                  </h3>
                </div>
              </Col>
              <Col>
                <div className="text-center">
                  <h6 className="header-pretitle">Amount Due</h6>
                  <h3 className="header-title">
                    <span className="animated fadeIn" key={outstandingAmount}>
                      {formatMoney(outstandingAmount)}
                    </span>
                  </h3>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <ValidInvoices
        paymentMethods={paymentMethods}
        batchPay={batchPay}
        hide={hide}
        setBatchPay={setBatchPay}
      />
    </>
  );
}
function InvalidInvoices({ hide, invalidInvoices, setBatchPay, batchPay }) {
  return (
    <>
      <ModalBody className="p-0">
        <div className="p-4 bg-lighter border-bottom">
          <h3 className="text-danger">Invalid Invoices</h3>
          <div className="mb-3">
            Some invoices may not be eligible for payment. Even invoices in an
            "Open" status may have already been paid in full or have other
            issues that prevent payment. The following invoices will be removed
            from the batch before continuing.
          </div>
          <div>
            Contact Straightline at{' '}
            <a href="mailto:accounting@bookstraightline.com">
              accounting@bookstraightline.com
            </a>{' '}
            with any questions.
          </div>
        </div>
        <div className="table-responsive border-bottom">
          <Table className="table table-nowrap card-table">
            <TableHead>
              <TableRow>
                <TableHeader className="py-3">Shipment</TableHeader>
                <TableHeader className="py-3">Pickup</TableHeader>
                <TableHeader className="py-3">Delivery</TableHeader>
                <TableHeader className="py-3 text-right">Amount</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody className="list">
              {invalidInvoices.map((invoice) => {
                const { shipment } = invoice;

                if (!shipment) {
                  return (
                    <InvoiceWithoutShipment
                      key={invoice.id}
                      invoice={invoice}
                      compact
                    />
                  );
                }

                return (
                  <TableRow key={invoice.id}>
                    <TableData>
                      <div>
                        <span className="text-muted">SL#: </span>
                        {shipment.id}
                      </div>
                      <div>
                        <span className="text-muted">REF#: </span>
                        {truncateText(shipment.reference_number) || (
                          <span className="text-muted">NA</span>
                        )}
                      </div>
                    </TableData>
                    <TableData>
                      <div>
                        {`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}
                        {shipment.pickup_address.name && (
                          <span
                            id={`pickup-name-${shipment.id}`}
                            className="ml-1"
                          >
                            <Icon type="info" className="text-secondary" />
                            <UncontrolledTooltip
                              target={`pickup-name-${shipment.id}`}
                            >
                              {shipment.pickup_address.name}
                            </UncontrolledTooltip>
                          </span>
                        )}
                      </div>
                      <div>
                        {formatDate(shipment.pickup_date, 'LLL do, yyyy')}
                      </div>
                    </TableData>
                    <TableData
                      className="px-3 px-xl-4"
                      style={{ width: '1%', position: 'relative' }}
                    >
                      <div style={{ position: 'absolute', left: -12, top: 21 }}>
                        <Icon
                          type="arrow-right text-success"
                          style={{ fontSize: 25 }}
                        />
                      </div>
                      <div>
                        {`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}
                        {shipment.delivery_address.name && (
                          <span
                            id={`delivery-name-${shipment.id}`}
                            className="ml-1"
                          >
                            <Icon type="info" className="text-secondary" />
                            <UncontrolledTooltip
                              target={`delivery-name-${shipment.id}`}
                            >
                              {shipment.delivery_address.name}
                            </UncontrolledTooltip>
                          </span>
                        )}
                      </div>
                      <div>{formatDate(shipment.due_date, 'LLL do, yyyy')}</div>
                    </TableData>
                    <TableData className="text-right">
                      <p className="mb-0">
                        {formatMoney(invoice.due_amount)}{' '}
                        <span className="small text-muted">due</span>
                      </p>
                      <p className="mb-0">
                        {formatMoney(invoice.paid_amount + invoice.fee_amount)}{' '}
                        <span className="small text-muted">paid</span>
                      </p>
                    </TableData>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <Button color="secondary" onClick={hide}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => {
            const validInvoices = batchPay.filter((invoice) => {
              return !invalidInvoices.find((inv) => inv.id === invoice.id);
            });
            setBatchPay(validInvoices);
            if (validInvoices.length === 0) {
              hide();
            }
          }}
        >
          Remove {invalidInvoices.length} Invoice
          {invalidInvoices.length === 1 ? '' : 's'}
        </Button>
      </ModalFooter>
    </>
  );
}

function InvoiceErrors({ hide, errors, setBatchPay, batchPay }) {
  return (
    <>
      <ModalBody className="p-0">
        <div className="p-4 bg-lighter border-bottom">
          <h3 className="text-danger">Invalid Invoices</h3>
          <div className="mb-3">
            There were errors processing the following invoices. Straightline
            has been notified and will reach out if additional information is
            needed.
          </div>
          <div>
            Contact Straightline at{' '}
            <a href="mailto:accounting@bookstraightline.com">
              accounting@bookstraightline.com
            </a>{' '}
            with any questions.
          </div>
        </div>
        <div className="table-responsive border-bottom">
          <Table className="table table-nowrap card-table">
            <TableHead>
              <TableRow>
                <TableHeader className="py-3">Shipment</TableHeader>
                <TableHeader className="py-3">Pickup</TableHeader>
                <TableHeader className="py-3">Delivery</TableHeader>
                <TableHeader className="py-3 text-right">Amount</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody className="list">
              {errors.rejected.map(({ shipment_invoice_id, error }) => {
                const invoice = batchPay.find(
                  (inv) => inv.id === shipment_invoice_id
                );
                const { shipment } = invoice;

                if (!shipment) {
                  return (
                    <InvoiceWithoutShipment
                      key={invoice.id}
                      invoice={invoice}
                      error={error}
                      compact
                    />
                  );
                }

                return (
                  <TableRow key={invoice.id}>
                    <TableData>
                      <div>
                        <span className="text-muted">SL#: </span>
                        {shipment.id}
                      </div>
                      <div className="text-danger">
                        {error.message || 'Unknown error'}
                      </div>
                    </TableData>
                    <TableData>
                      <div>
                        {`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}
                        {shipment.pickup_address.name && (
                          <span
                            id={`pickup-name-${shipment.id}`}
                            className="ml-1"
                          >
                            <Icon type="info" className="text-secondary" />
                            <UncontrolledTooltip
                              target={`pickup-name-${shipment.id}`}
                            >
                              {shipment.pickup_address.name}
                            </UncontrolledTooltip>
                          </span>
                        )}
                      </div>
                      <div>
                        {formatDate(shipment.pickup_date, 'LLL do, yyyy')}
                      </div>
                    </TableData>
                    <TableData
                      className="px-3 px-xl-4"
                      style={{ width: '1%', position: 'relative' }}
                    >
                      <div style={{ position: 'absolute', left: -12, top: 21 }}>
                        <Icon
                          type="arrow-right text-success"
                          style={{ fontSize: 25 }}
                        />
                      </div>
                      <div>
                        {`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}
                        {shipment.delivery_address.name && (
                          <span
                            id={`delivery-name-${shipment.id}`}
                            className="ml-1"
                          >
                            <Icon type="info" className="text-secondary" />
                            <UncontrolledTooltip
                              target={`delivery-name-${shipment.id}`}
                            >
                              {shipment.delivery_address.name}
                            </UncontrolledTooltip>
                          </span>
                        )}
                      </div>
                      <div>{formatDate(shipment.due_date, 'LLL do, yyyy')}</div>
                    </TableData>
                    <TableData className="text-right">
                      <p className="mb-0">
                        {formatMoney(invoice.due_amount)}{' '}
                        <span className="small text-muted">due</span>
                      </p>
                      <p className="mb-0">
                        {formatMoney(invoice.paid_amount + invoice.fee_amount)}{' '}
                        <span className="small text-muted">paid</span>
                      </p>
                    </TableData>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <Button color="secondary" onClick={hide}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => {
            hide();
            setBatchPay(null);
          }}
        >
          Close
        </Button>
      </ModalFooter>
    </>
  );
}

function ValidInvoices({ paymentMethods, batchPay, hide, setBatchPay }) {
  const app = useApp();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState(null);

  useEffect(() => {
    if (paymentMethods?.length > 0 && !selectedPaymentMethod) {
      const defaultMethod = paymentMethods.find((method) => {
        return method.default_method;
      });
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod);
      }
    }
  }, [paymentMethods, selectedPaymentMethod]);

  const outstandingAmount = batchPay.reduce((sum, invoice) => {
    return sum + (invoice.due_amount - invoice.paid_amount);
  }, 0);

  let feeAmount = 0;
  if (selectedPaymentMethod) {
    feeAmount = calculatePaymentFee(outstandingAmount, selectedPaymentMethod);
  }

  const totalAmount = outstandingAmount + feeAmount;

  const handlePayInvoice = async () => {
    if (!selectedPaymentMethod) {
      return;
    }

    try {
      setIsLoading(true);
      const result = await app.service('api/shipment-invoice-charges').create({
        shipment_invoice_ids: batchPay.map((invoice) => invoice.id),
        payment_method_id: selectedPaymentMethod.id
      });

      app.service('api/shipment-invoices').invalidateFind();
      app.service('api/shipment-stats').invalidateFind();
      app.service('api/shipment-invoice-charges').invalidateFind();

      if (result.rejected.length === 0) {
        toast.success('Payment successful');
        hide();
        setBatchPay(null);
        setIsLoading(false);
        return;
      }

      setErrors(result);
    } catch (error) {
      setIsLoading(false);
      toast.errors(error);
    }
  };

  if (errors) {
    return (
      <InvoiceErrors
        errors={errors}
        hide={hide}
        setBatchPay={setBatchPay}
        batchPay={batchPay}
      />
    );
  }

  return (
    <>
      <ModalBody className="p-0">
        <div className="p-4 bg-lighter border-bottom">
          <h3>Select Payment Method</h3>
          <div className="mb-3">
            Choose a payment method to pay these invoices. One charge will be
            made on this payment method for the total amount due, including any
            processing fees. Straightline will then allocate the payment to each
            invoice accordingly.
          </div>
          <div>
            Contact Straightline at{' '}
            <a href="mailto:accounting@bookstraightline.com">
              accounting@bookstraightline.com
            </a>{' '}
            with any questions.
          </div>
        </div>
        <div className="p-4">
          <PaymentMethodSelector
            paymentMethods={paymentMethods}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            disabled={isLoading}
            amount={outstandingAmount}
          />
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <Button color="secondary" onClick={hide} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handlePayInvoice}
          loading={isLoading}
          disabled={isLoading}
          style={{ minWidth: 200, fontSize: 18 }}
        >
          <span className="mr-2">Pay</span>
          {selectedPaymentMethod ? (
            <span className="animated fadeIn fast" key={totalAmount}>
              {formatMoney(totalAmount)}
            </span>
          ) : (
            'Invoices'
          )}
        </Button>
      </ModalFooter>
    </>
  );
}

function SearchBar({ queryState, query, total, tab, batchPay, setBatchPay }) {
  const { setInState, opInState, delInState } = queryState;
  const { isXs, isSm, isMd, isLg, isXl } = useScreenSize();
  const $search = query.$search || '';
  const [text, setText] = useState($search);
  const rangeWrapper = useRef(null);

  // eslint-disable-next-line
    const debouncedHandleChange = useCallback(
    debounce((value) => {
      opInState((newState) => {
        newState.del('query.$skip');
        const val = value && value.trim ? value.trim() : value;
        if (val) {
          newState.set('query.$search', val);
        } else {
          newState.del('query.$search');
        }
        return newState;
      });
    }, 300),
    []
  );

  const handleSearchChange = ({ target: { value } }) => {
    setText(value);
    debouncedHandleChange(value);
  };

  const handleSearchClear = () => {
    setText('');
    delInState('query.$search', 'query.$skip');
  };

  const ranges = useMemo(
    () => rangeConfig(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatDate(new Date(), 'dateOnly')]
  );

  const handleDateChange = ({ target: { value } }) => {
    opInState((newState) => {
      newState.del('query.$skip');

      if (!value || !value.length || !value[0] || !value[1]) {
        newState.del('query.due_date');
        return newState;
      }

      newState.set('query.due_date', {
        $between: [
          formatDate(value[0], 'dateOnly'),
          formatDate(value[1], 'dateOnly')
        ]
      });

      return newState;
    });
  };

  return (
    <div className="px-3" ref={rangeWrapper}>
      <Row className="align-items-center">
        <Col
          md="12"
          lg
          className={classNames('py-2', {
            'border-right': isLg || isXl,
            'border-bottom': isXs || isSm || isMd
          })}
        >
          <Row className="align-items-center">
            <Col>
              <Row className="align-items-center">
                <Col className="col-auto pr-0">
                  <Icon type="search" className="text-muted" />
                </Col>
                <Col>
                  <Input
                    value={text}
                    onChange={handleSearchChange}
                    className="form-control-flush"
                    placeholder="Search"
                  />
                </Col>
                {text && (
                  <Col className="col-auto p-0">
                    <Button
                      className="text-muted"
                      color="default"
                      type="button"
                      size="sm"
                      onClick={handleSearchClear}
                    >
                      <Icon type="x" />
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
        <Col
          md="12"
          lg="auto"
          className="py-3 py-lg-2 d-flex align-items-center"
        >
          <Row noGutters className="mb-n1">
            <Col xs="auto" className="mr-2 mb-1">
              <TablePaginationBtns
                total={total}
                limit={10}
                skip={query.$skip || 0}
                setSkip={($skip) => setInState('query.$skip', $skip)}
              />
            </Col>
            {/* <Col xs="auto" className="mr-2 mb-1">
              <SortButton queryState={queryState} sortLabels={sortLabels} />
            </Col> */}
            <Col xs="auto" className="mr-2 mb-1">
              <ModeButton query={query} queryState={queryState} />
            </Col>
            <Col xs="auto" className="mr-2 mb-1">
              <StatusButton
                query={query}
                queryState={queryState}
                disabled={tab !== 'all' || !!batchPay}
              />
            </Col>
            <Col xs="auto" className="mr-2 mb-1">
              <DateRange
                disabled={tab !== 'all'}
                right
                placeholder="Due Date"
                size="sm"
                name="due_date"
                value={query.due_date?.$between || []}
                ranges={ranges}
                onChange={handleDateChange}
                wrapper={rangeWrapper}
              />
            </Col>
            <Col xs="auto" className="mb-1">
              <Button
                size="sm"
                color="primary"
                disabled={!!batchPay}
                onClick={() => {
                  if (batchPay) {
                    return setBatchPay(null);
                  }
                  setBatchPay([]);
                }}
              >
                Batch Pay
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
}

const { LTL, FTL, PARCEL } = MODE;

const modeLabels = {
  all: 'All',
  [LTL]: 'LTL',
  [FTL]: 'FTL',
  [PARCEL]: 'Parcel'
};

const ModeButton = ({ query, queryState }) => {
  const { setInState, delInState } = queryState;
  const mode = query['$shipment.mode$'] || 'all';
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(!dropdownOpen);

  const handleModeChange = (newMode) => {
    if (newMode === 'all') {
      return delInState(['query', '$shipment.mode$']);
    }
    setInState(['query', '$shipment.mode$'], newMode);
  };

  return (
    <Dropdown isOpen={dropdownOpen} toggle={toggle}>
      <DropdownToggle size="sm" color="light">
        <span>Mode: {modeLabels[mode]}</span>
      </DropdownToggle>
      <DropdownMenu right>
        {Object.entries(modeLabels).map(([key, value]) => {
          const isActive = key === mode;

          return (
            <DropdownItem
              key={key}
              onClick={() => handleModeChange(key)}
              disabled={isActive}
              className={isActive ? 'text-dark' : ''}
            >
              {value}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};

const { PAID, OPEN, VOID, UNCOLLECTIBLE } = INVOICE_STATUS;

const statusLabels = {
  all: 'All',
  [PAID]: 'Paid',
  [OPEN]: 'Open',
  [VOID]: 'Void',
  [UNCOLLECTIBLE]: 'Uncollectible'
};

const StatusButton = ({ query, queryState, disabled }) => {
  const { setInState, delInState } = queryState;
  const status = query.status || 'all';
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(!dropdownOpen);

  const handleStatusChange = (newStatus) => {
    if (newStatus === 'all') {
      return delInState('query.status');
    }
    setInState('query.status', newStatus);
  };

  return (
    <Dropdown isOpen={dropdownOpen} toggle={toggle}>
      <DropdownToggle size="sm" color="light" disabled={disabled}>
        <span>Status: {statusLabels[status]}</span>
      </DropdownToggle>
      <DropdownMenu right>
        {Object.entries(statusLabels).map(([key, value]) => {
          const isActive = key === status;
          return (
            <DropdownItem
              key={key}
              onClick={() => handleStatusChange(key)}
              disabled={isActive}
              className={isActive ? 'text-dark' : ''}
            >
              {value}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};

function InvoiceWithoutShipment({
  invoice,
  error,
  compact = false,
  batchPay,
  selected,
  onClick
}) {
  return (
    <TableRow className={onClick ? 'pointer' : undefined} onClick={onClick}>
      {!compact && batchPay && (
        <TableData style={{ width: '1%' }} className="pl-3">
          <Icon
            type={selected ? 'check-circle text-primary' : 'circle text-muted'}
            style={{ fontSize: 20 }}
          />
        </TableData>
      )}
      {!compact && (
        <TableData>
          <Badge color={statusColor[invoice.status]} style={{ padding: 8.5 }}>
            {invoice.status.charAt(0).toUpperCase() +
              invoice.status.slice(1).toLowerCase()}
          </Badge>
        </TableData>
      )}
      <TableData>
        <div>Invoice #{invoice.id}</div>
        <div>
          <span className="text-muted">SL#: </span>
          {invoice.shipment_id}
        </div>
        {error && (
          <div className="text-danger">{error.message || 'Unknown error'}</div>
        )}
      </TableData>
      <TableData colSpan={compact ? 2 : 3}>
        Shipment information is not available for Shipment #
        {invoice.shipment_id}.
        <br />
        This invoice may be for a shipment assigned to another shipper.
      </TableData>
      <TableData>
        <p className="mb-0">
          {formatMoney(invoice.due_amount)}{' '}
          <span className="small text-muted">due</span>
        </p>
        <p className="mb-0">
          {formatMoney(invoice.paid_amount + invoice.fee_amount)}{' '}
          <span className="small text-muted">paid</span>
        </p>
      </TableData>
      {!compact && <TableData>{formatDate(invoice.due_date)}</TableData>}
    </TableRow>
  );
}

function InvoicesTable({ invoices, query, queryState, batchPay, setBatchPay }) {
  const history = useHistory();
  const { assignState } = queryState;

  const handleSort = ({ name, value }) => {
    assignState({ $sort: { [name]: value } });
  };

  return (
    <div className="table-responsive">
      <Table className="table table-nowrap card-table table-hover">
        <TableHead>
          <TableRow>
            {batchPay && <TableHeader className="py-3"></TableHeader>}
            <TableHeader
              className="py-3"
              sortName="status"
              sort={query.$sort?.['status']}
              onClick={handleSort}
            >
              Status
            </TableHeader>
            <TableHeader className="py-3">Shipment</TableHeader>
            <TableHeader className="py-3">Pickup</TableHeader>
            <TableHeader className="py-3">Delivery</TableHeader>
            <TableHeader className="py-3">Carrier</TableHeader>
            <TableHeader className="py-3">Amount</TableHeader>
            <TableHeader
              className="py-3"
              sortName="due_date"
              // sort={query.$sort?.due_date}
              // onClick={handleSort}
            >
              Due Date
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody className="list">
          {invoices.data.map((invoice) => {
            const { shipment } = invoice;
            const selected = batchPay?.find((i) => i.id === invoice.id);

            const onClick = () => {
              if (!batchPay) {
                if (shipment) {
                  history.push(`/shipments/${invoice.shipment_id}?tab=invoice`);
                } else {
                  window.open(
                    invoice.secure_url,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }
                return;
              }

              if (!selected) {
                setBatchPay([...batchPay, invoice]);
                return;
              }

              setBatchPay(batchPay.filter((i) => i.id !== invoice.id));
            };

            if (!shipment) {
              return (
                <InvoiceWithoutShipment
                  key={invoice.id}
                  invoice={invoice}
                  batchPay={batchPay}
                  selected={selected}
                  onClick={onClick}
                />
              );
            }

            return (
              <TableRow key={invoice.id} className="pointer" onClick={onClick}>
                {batchPay && (
                  <TableData style={{ width: '1%' }} className="pl-3">
                    {selected ? (
                      <Icon
                        type="check-circle text-primary animated fadeIn faster"
                        style={{ fontSize: 20 }}
                      />
                    ) : (
                      <Icon type="circle text-muted" style={{ fontSize: 20 }} />
                    )}
                  </TableData>
                )}
                <TableData>
                  <Badge
                    color={statusColor[invoice.status]}
                    style={{ padding: 8.5 }}
                  >
                    {invoice.status.charAt(0).toUpperCase() +
                      invoice.status.slice(1).toLowerCase()}
                  </Badge>
                </TableData>
                <TableData>
                  <div>
                    <span className="text-muted">SL#: </span>
                    {shipment.id}
                  </div>
                  <div>
                    <span className="text-muted">REF#: </span>
                    {truncateText(shipment.reference_number) || (
                      <span className="text-muted">NA</span>
                    )}
                  </div>
                </TableData>
                <TableData>
                  <div>
                    {`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}
                    {shipment.pickup_address.name && (
                      <span id={`pickup-name-${shipment.id}`} className="ml-1">
                        <Icon type="info" className="text-secondary" />
                        <UncontrolledTooltip
                          target={`pickup-name-${shipment.id}`}
                        >
                          {shipment.pickup_address.name}
                        </UncontrolledTooltip>
                      </span>
                    )}
                  </div>
                  <div>{formatDate(shipment.pickup_date, 'LLL do, yyyy')}</div>
                </TableData>
                <TableData
                  className="px-3 px-xl-4"
                  style={{ width: '1%', position: 'relative' }}
                >
                  <div style={{ position: 'absolute', left: -12, top: 21 }}>
                    <Icon
                      type="arrow-right text-success"
                      style={{ fontSize: 25 }}
                    />
                  </div>
                  <div>
                    {`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}
                    {shipment.delivery_address.name && (
                      <span
                        id={`delivery-name-${shipment.id}`}
                        className="ml-1"
                      >
                        <Icon type="info" className="text-secondary" />
                        <UncontrolledTooltip
                          target={`delivery-name-${shipment.id}`}
                        >
                          {shipment.delivery_address.name}
                        </UncontrolledTooltip>
                      </span>
                    )}
                  </div>
                  <div>{formatDate(shipment.due_date, 'LLL do, yyyy')}</div>
                </TableData>
                <TableData>
                  <div>
                    {(shipment.carrier && shipment.carrier.name) || (
                      <div className="text-muted">Carrier not assigned</div>
                    )}
                  </div>
                  <div>
                    {shipment.status.label || (
                      <div className="text-muted">No status</div>
                    )}
                  </div>
                </TableData>
                <TableData>
                  <p className="mb-0">
                    {formatMoney(invoice.due_amount)}{' '}
                    <span className="small text-muted">due</span>
                  </p>
                  <p className="mb-0">
                    {formatMoney(invoice.paid_amount + invoice.fee_amount)}{' '}
                    <span className="small text-muted">paid</span>
                  </p>
                </TableData>
                <TableData>
                  <p className="mb-0">{formatDate(invoice.due_date)}</p>
                  <React.Fragment>
                    {(() => {
                      if (invoice.status === INVOICE_STATUS.PAID) {
                        return <div className="mb-0 text-success">Paid</div>;
                      }

                      if (invoice.status === INVOICE_STATUS.OPEN) {
                        if (new Date(invoice.due_date) <= new Date()) {
                          return (
                            <div className="mb-0 text-danger">Overdue</div>
                          );
                        }
                        return (
                          <div className="mb-0 text-primary">
                            Due {formatDistanceToNow(invoice.due_date)}
                          </div>
                        );
                      }

                      if (
                        [
                          INVOICE_STATUS.VOID,
                          INVOICE_STATUS.UNCOLLECTIBLE
                        ].includes(invoice.status)
                      ) {
                        return <div className="mb-0 text-muted">Voided</div>;
                      }
                    })()}
                  </React.Fragment>
                </TableData>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
